import { env } from '@/config/env';

/**
 * Loads the Google Maps JavaScript API, once.
 *
 * This is the API the old portal used (through `@react-google-maps/api`) and
 * the one the client's key is enabled for. It is *not* the Maps Embed API —
 * that is a separate product with its own switch in Google Cloud, which is why
 * an `<iframe src="maps/embed/v1/...">` renders blank against a key that works
 * perfectly well elsewhere.
 *
 * No npm dependency: the wrapper library exists to turn this one script tag
 * into a hook, and one promise does the same job.
 */

let pending: Promise<typeof google.maps> | null = null;

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    if (!env.geoApiKey) {
      reject(new Error('No Google Maps key is configured.'));
      return;
    }

    // Another part of the page may have loaded it already.
    if (typeof google !== 'undefined' && google.maps) {
      resolve(google.maps);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${env.geoApiKey}&v=weekly`;
    script.async = true;
    script.onload = () => {
      if (typeof google !== 'undefined' && google.maps) resolve(google.maps);
      else reject(new Error('Google Maps loaded without an API.'));
    };
    script.onerror = () => {
      // Let the next attempt try again rather than caching the failure.
      pending = null;
      reject(new Error('Google Maps could not be loaded.'));
    };
    document.head.appendChild(script);
  });

  return pending;
}
